import React, { useState, useMemo } from 'react';
import useBreakpoint from '../hooks/useBreakpoint';
import { useTheme } from '../ThemeContext';
import { EXPENSE_CATEGORIES, INCOME_CATEGORIES, getCurrencySymbol } from '../categories';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

const newId = () => Math.random().toString(36).substr(2, 9);
const TODAY = new Date().toISOString().split('T')[0];

const ANNUAL = { Daily: 365, Weekly: 52, Biweekly: 26, Monthly: 12, Quarterly: 4, 'Semi-annually': 2, Yearly: 1 };
const toMonthly = (item) => (item.amount * ANNUAL[item.frequency]) / 12;

function fmtDol(n, sym) {
  const s = sym || '$';
  return (n < 0 ? '-' : '') + s + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

export default function SpendingLog({ spendingLog, budgetItems, onSpendingLogChange, isPro, onUpgradeClick, currency }) {
  const bp = useBreakpoint();
  const isMobile = bp === 'mobile';
  const isDesktop = bp === 'desktop';
  const { t } = useTheme();
  const sym = getCurrencySymbol(currency);

  const [draft, setDraft] = useState({ date: TODAY, name: '', category: 'Food', amount: '', type: 'expense' });
  const [showAdd, setShowAdd] = useState(false);
  const [filterMonth, setFilterMonth] = useState(() => TODAY.slice(0, 7));

  const addEntry = () => {
    if (!draft.name.trim() || !draft.amount) return;
    onSpendingLogChange([
      { id: newId(), ...draft, amount: parseFloat(draft.amount) },
      ...spendingLog,
    ]);
    setDraft({ date: TODAY, name: '', category: 'Food', amount: '', type: 'expense' });
    setShowAdd(false);
  };

  const deleteEntry = (id) => onSpendingLogChange(spendingLog.filter(e => e.id !== id));

  // Filter log to selected month
  const filteredLog = useMemo(() =>
    spendingLog.filter(e => e.date && e.date.startsWith(filterMonth))
      .sort((a, b) => b.date.localeCompare(a.date)),
  [spendingLog, filterMonth]);

  // Budget vs actual for the selected month
  const budgetByCategory = useMemo(() => {
    const bycat = {};
    budgetItems.filter(i => i.type !== 'income').forEach(i => {
      const cat = i.category || 'Other Expenses';
      bycat[cat] = (bycat[cat] || 0) + toMonthly(i);
    });
    return bycat;
  }, [budgetItems]);

  const actualByCategory = useMemo(() => {
    const bycat = {};
    filteredLog.filter(e => e.type === 'expense').forEach(e => {
      const cat = e.category || 'Other';
      bycat[cat] = (bycat[cat] || 0) + (e.amount || 0);
    });
    return bycat;
  }, [filteredLog]);

  // Categories that appear in either budget or actual
  const allCats = useMemo(() => {
    const cats = new Set([...Object.keys(budgetByCategory), ...Object.keys(actualByCategory)]);
    return [...cats].sort();
  }, [budgetByCategory, actualByCategory]);

  // Monthly trend data (last 6 months with data)
  const trendData = useMemo(() => {
    const monthMap = {};
    spendingLog.forEach(e => {
      if (!e.date) return;
      const mo = e.date.slice(0, 7);
      if (!monthMap[mo]) monthMap[mo] = { income: 0, expenses: 0 };
      if (e.type === 'expense') monthMap[mo].expenses += e.amount || 0;
      else monthMap[mo].income += e.amount || 0;
    });
    return Object.entries(monthMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([mo, v]) => ({
        month: mo.slice(5) + '/' + mo.slice(2, 4),
        Income: Math.round(v.income),
        Spending: Math.round(v.expenses),
      }));
  }, [spendingLog]);

  const pagePadding = isDesktop ? '20px' : isMobile ? '10px 10px' : '16px';
  const tdPadding = isMobile ? '8px 8px' : '10px 14px';
  const thStyle = {
    padding: tdPadding, textAlign: 'left', fontWeight: '700', fontSize: '10px',
    letterSpacing: '0.05em', textTransform: 'uppercase', color: t.textSecondary,
  };

  // Locked state for free users
  if (!isPro) {
    return (
      <div style={{ minHeight: '100vh', background: t.pageBg, padding: pagePadding }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '800', letterSpacing: '-0.02em', color: t.text, marginBottom: '8px' }}>Spending Log</h1>
          <div style={{
            background: t.card, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            padding: '40px 32px', textAlign: 'center', marginTop: '24px',
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px', lineHeight: 1 }}>
              <span style={{ fontSize: '32px', fontWeight: '700', color: t.text }}>Spending Log</span>
            </div>
            <p style={{ color: t.textMuted, fontSize: '16px', lineHeight: '1.6', maxWidth: '480px', margin: '0 auto 12px' }}>
              Log your actual transactions, compare spending against your budget by category, and track monthly trends over time.
            </p>
            <p style={{ color: t.textMuted, fontSize: '14px', marginBottom: '24px' }}>
              Available with Budget Planner Pro.
            </p>
            <button
              onClick={onUpgradeClick}
              style={{
                background: 'linear-gradient(135deg, #6a11cb, #2575fc)', color: 'white', border: 'none',
                borderRadius: '10px', padding: '13px 32px', fontSize: '15px', fontWeight: '700',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              Upgrade to Pro — $5.99/mo
            </button>
            <div style={{ marginTop: '20px', display: 'flex', flexDirection: 'column', gap: '8px', maxWidth: '360px', margin: '20px auto 0', textAlign: 'left' }}>
              {[
                'Log actual transactions with category, amount, and date',
                'Budget vs. actual table by category',
                'Monthly spending trend chart',
                'All data synced to your account',
              ].map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', color: t.textSecondary }}>
                  <span style={{ color: '#1b6d24', fontWeight: '700', flexShrink: 0 }}>✓</span>
                  <span>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: t.pageBg, padding: pagePadding }}>
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '800', letterSpacing: '-0.02em', color: t.text, margin: 0 }}>Spending Log</h1>
          <button
            onClick={() => setShowAdd(v => !v)}
            style={{
              background: showAdd ? '#888' : '#1b6d24', color: 'white', border: 'none',
              borderRadius: '20px', padding: '10px 24px', fontSize: '14px', fontWeight: '600',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {showAdd ? 'Cancel' : '+ Add Transaction'}
          </button>
        </div>

        {/* Add transaction form */}
        {showAdd && (
          <div style={{ background: t.card, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Date</label>
                <input type="date" value={draft.date} onChange={e => setDraft(p => ({ ...p, date: e.target.value }))}
                  style={{ border: `1px solid ${t.borderFaint}`, borderRadius: '7px', padding: '8px 10px', fontSize: '14px', fontFamily: 'inherit', color: t.text, background: t.card, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '1 1 140px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</label>
                <input type="text" value={draft.name} onChange={e => setDraft(p => ({ ...p, name: e.target.value }))}
                  placeholder="Transaction name..." autoFocus
                  style={{ border: `1px solid ${t.borderFaint}`, borderRadius: '7px', padding: '8px 10px', fontSize: '14px', fontFamily: 'inherit', color: t.text, background: t.card, outline: 'none' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</label>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['expense', 'income'].map(tp => (
                    <button key={tp} onClick={() => setDraft(p => ({ ...p, type: tp, category: tp === 'income' ? 'Employment' : 'Food' }))}
                      style={{ border: 'none', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                        background: draft.type === tp ? (tp === 'income' ? '#1b6d24' : '#ba1a1a') : t.borderLight,
                        color: draft.type === tp ? 'white' : t.textMuted }}>
                      {tp === 'income' ? 'Income' : 'Expense'}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 auto' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Category</label>
                <select value={draft.category} onChange={e => setDraft(p => ({ ...p, category: e.target.value }))}
                  style={{ border: `1px solid ${t.borderFaint}`, borderRadius: '7px', padding: '8px 10px', fontSize: '14px', fontFamily: 'inherit', color: t.text, background: t.card, outline: 'none', cursor: 'pointer' }}>
                  {(draft.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: '0 0 120px' }}>
                <label style={{ fontSize: '11px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount</label>
                <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${t.borderFaint}`, borderRadius: '7px', overflow: 'hidden', background: t.card }}>
                  <span style={{ padding: '0 8px', color: t.textMuted, borderRight: `1px solid ${t.borderFaint}`, fontSize: '14px', lineHeight: '36px', userSelect: 'none' }}>{sym}</span>
                  <input type="number" value={draft.amount} onChange={e => setDraft(p => ({ ...p, amount: e.target.value }))}
                    placeholder="0.00" min="0" step="0.01"
                    style={{ border: 'none', outline: 'none', padding: '8px 10px', fontSize: '14px', fontFamily: 'inherit', color: t.text, background: 'transparent', width: '80px' }}
                    onKeyDown={e => e.key === 'Enter' && addEntry()} />
                </div>
              </div>
              <button onClick={addEntry}
                style={{ background: '#0061a4', color: 'white', border: 'none', borderRadius: '8px', padding: '9px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
                Add
              </button>
            </div>
          </div>
        )}

        {/* Month filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <label style={{ fontSize: '12px', fontWeight: '600', color: t.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Month</label>
          <input type="month" value={filterMonth} onChange={e => setFilterMonth(e.target.value)}
            style={{ border: `1px solid ${t.borderFaint}`, borderRadius: '7px', padding: '6px 10px', fontSize: '14px', fontFamily: 'inherit', color: t.text, background: t.card, outline: 'none' }} />
          <span style={{ fontSize: '12px', color: t.textMuted }}>{filteredLog.length} transaction{filteredLog.length !== 1 ? 's' : ''}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isDesktop ? '1fr 1fr' : '1fr', gap: '16px' }}>
          {/* Transaction list */}
          <div style={{ background: t.card, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${t.borderLight}` }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: t.text }}>Transactions</span>
            </div>
            {filteredLog.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: t.textFaintest, fontSize: '14px' }}>
                No transactions logged for this month.
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {filteredLog.map(entry => (
                  <div key={entry.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', borderBottom: `1px solid ${t.borderLight}`, gap: '8px' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '600', fontSize: '14px', color: t.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.name}</div>
                      <div style={{ fontSize: '11px', color: t.textMuted, marginTop: '2px' }}>
                        {entry.category && <span style={{ background: t.tableHeader, borderRadius: '8px', padding: '1px 6px', marginRight: '6px', color: t.textSecondary }}>{entry.category}</span>}
                        {entry.date}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <span style={{ fontWeight: '700', fontSize: '14px', color: entry.type === 'income' ? '#1b6d24' : '#ba1a1a' }}>
                        {entry.type === 'income' ? '+' : '-'}{fmtDol(entry.amount, sym)}
                      </span>
                    </div>
                    <button onClick={() => deleteEntry(entry.id)} style={{ background: 'none', border: 'none', color: t.textFaintest, cursor: 'pointer', fontSize: '18px', padding: '0 4px', fontFamily: 'inherit', lineHeight: 1, flexShrink: 0 }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Budget vs Actual */}
          <div style={{ background: t.card, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
            <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${t.borderLight}` }}>
              <span style={{ fontSize: '14px', fontWeight: '700', color: t.text }}>Budget vs. Actual</span>
            </div>
            {allCats.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: t.textFaintest, fontSize: '14px' }}>
                Add budget items and log transactions to see a comparison.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: t.tableHeader }}>
                      <th style={thStyle}>Category</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Budgeted</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Actual</th>
                      <th style={{ ...thStyle, textAlign: 'right' }}>Difference</th>
                      <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCats.map(cat => {
                      const budget = budgetByCategory[cat] || 0;
                      const actual = actualByCategory[cat] || 0;
                      const diff = budget - actual;
                      const isUnbudgeted = budget === 0 && actual > 0;
                      let statusLabel, statusColor;
                      if (isUnbudgeted) {
                        statusLabel = 'Unbudgeted'; statusColor = t.textMuted;
                      } else if (actual > budget) {
                        statusLabel = 'Over'; statusColor = '#ba1a1a';
                      } else if (actual >= budget * 0.9) {
                        statusLabel = 'On Track'; statusColor = t.textSecondary;
                      } else {
                        statusLabel = 'Under'; statusColor = '#1b6d24';
                      }
                      return (
                        <tr key={cat} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                          <td style={{ padding: tdPadding, fontSize: '13px', color: t.textSecondary }}>{cat}</td>
                          <td style={{ padding: tdPadding, textAlign: 'right', fontSize: '13px', color: t.textSecondary }}>{fmtDol(budget, sym)}</td>
                          <td style={{ padding: tdPadding, textAlign: 'right', fontSize: '13px', fontWeight: '600', color: actual > budget ? '#ba1a1a' : t.text }}>{fmtDol(actual, sym)}</td>
                          <td style={{ padding: tdPadding, textAlign: 'right', fontSize: '13px', fontWeight: '600', color: diff >= 0 ? '#1b6d24' : '#ba1a1a' }}>
                            {diff >= 0 ? '+' : ''}{fmtDol(Math.abs(diff), sym)}
                          </td>
                          <td style={{ padding: tdPadding, textAlign: 'center', fontSize: '11px', fontWeight: '700', color: statusColor }}>{statusLabel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {allCats.length > 0 && (() => {
                    const totalBudget = allCats.reduce((s, c) => s + (budgetByCategory[c] || 0), 0);
                    const totalActual = allCats.reduce((s, c) => s + (actualByCategory[c] || 0), 0);
                    const totalDiff = totalBudget - totalActual;
                    return (
                      <tfoot>
                        <tr style={{ borderTop: `2px solid ${t.border}`, background: t.tableHeader }}>
                          <td style={{ padding: tdPadding, fontSize: '13px', fontWeight: '700', color: t.text }}>Total</td>
                          <td style={{ padding: tdPadding, textAlign: 'right', fontSize: '13px', fontWeight: '700', color: t.text }}>{fmtDol(totalBudget, sym)}</td>
                          <td style={{ padding: tdPadding, textAlign: 'right', fontSize: '13px', fontWeight: '700', color: totalActual > totalBudget ? '#ba1a1a' : t.text }}>{fmtDol(totalActual, sym)}</td>
                          <td style={{ padding: tdPadding, textAlign: 'right', fontSize: '13px', fontWeight: '700', color: totalDiff >= 0 ? '#1b6d24' : '#ba1a1a' }}>
                            {totalDiff >= 0 ? '+' : ''}{fmtDol(Math.abs(totalDiff), sym)}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    );
                  })()}
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Monthly trend chart — show if 2+ months of data */}
        {trendData.length >= 2 && (
          <div style={{ background: t.card, borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', padding: '20px', marginTop: '16px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '15px', fontWeight: '700', color: t.text }}>Monthly Spending Trend</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={trendData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={n => sym + (n >= 1000 ? (n/1000).toFixed(0)+'k' : n)} />
                <Tooltip formatter={(v) => fmtDol(v, sym)} />
                <Line type="monotone" dataKey="Income" stroke="#1b6d24" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Spending" stroke="#ba1a1a" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
}
